import {DigitalAssetVisual} from '@cohort/components-xps/components/DigitalAssetVisual';
import {classNames} from '@cohort/shared-frontend/utils/classNames';
import SecondaryButton from '@cohort/wallet/components/button/SecondaryButton';
import DigitalAssetTitleBlock from '@cohort/wallet/components/DigitalAssetTitleBlock';
import {
  HoritzontalDirections,
  VerticalDirections,
} from '@cohort/wallet/components/navigation/directions';
import {
  DropdownChildrenLink,
  DropdownComponent,
} from '@cohort/wallet/components/navigation/DropdownNavigation';
import {PerkAccessesDetails} from '@cohort/wallet/components/space/nftTransfer/PerkAccessesDetails';
import {ownershipsKeys} from '@cohort/wallet/hooks/api/Ownerships';
import useNotify from '@cohort/wallet/hooks/notify';
import {useApi} from '@cohort/wallet/hooks/useApi';
import useSpaceContext from '@cohort/wallet/hooks/useSpaceContext';
import useThemeContext from '@cohort/wallet/hooks/useThemeContext';
import {OwnershipsApi} from '@cohort/wallet/lib/Endpoints';
import {getNewNftTransferRoute, getNftDetailRoute} from '@cohort/wallet/lib/Pages';
import {canShare, shareNft} from '@cohort/wallet/lib/Share';
import type {TrackingConfig} from '@cohort/wallet/lib/Tracking';
import {trackError} from '@cohort/wallet/lib/Tracking';
import {nftObtentionDate} from '@cohort/wallet/lib/Utils';
import type {OwnershipWDto} from '@cohort/wallet-schemas/ownership';
import {Menu} from '@headlessui/react';
import {ArrowRight, ArrowSquareOut, DotsThree, Share, X} from '@phosphor-icons/react';
import {useQueryClient} from '@tanstack/react-query';
import React, {Fragment} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

interface DigitalAssetExternalLinkProps {
  text: string;
  href: string;
  tracking: TrackingConfig;
}

const DigitalAssetExternalLink: React.FC<DigitalAssetExternalLinkProps> = ({
  text,
  href,
  tracking,
}) => {
  const {hasDarkBg} = useThemeContext();

  return (
    <div>
      <DropdownChildrenLink text={text} href={href} tracking={tracking}>
        <ArrowSquareOut
          className={classNames(
            hasDarkBg
              ? 'text-white/40 group-hover:text-white/50'
              : 'text-black/40 group-hover:text-black/50',
            'mr-3 h-5 w-5 '
          )}
          aria-hidden="true"
        />
      </DropdownChildrenLink>
    </div>
  );
};

interface DigitalAssetExternalLinksProps {
  ownership: OwnershipWDto;
}

const DigitalAssetExternalLinks: React.FC<DigitalAssetExternalLinksProps> = ({ownership}) => {
  const {t} = useTranslation('components', {keyPrefix: 'space.aboutDigitalAssetPanel'});

  return (
    <Fragment>
      {ownership.digitalAsset.nft?.blockExplorerUrl && (
        <DigitalAssetExternalLink
          href={ownership.digitalAsset.nft.blockExplorerUrl}
          text={t('viewPolygonscan')}
          tracking={{
            namespace: 'digitalAssets.detail.viewPolygonScan',
            metadata: {
              ownershipId: ownership.id,
            },
          }}
        />
      )}

      {ownership.digitalAsset.nft?.openseaUrl && (
        <DigitalAssetExternalLink
          href={ownership.digitalAsset.nft.openseaUrl}
          text={t('viewOpensea')}
          tracking={{
            namespace: 'digitalAssets.detail.viewOpensea',
            metadata: {
              ownershipId: ownership.id,
            },
          }}
        />
      )}
    </Fragment>
  );
};

interface AboutDigitalAssetPanelProps {
  ownership: OwnershipWDto;
}

const AboutDigitalAssetPanel: React.FC<AboutDigitalAssetPanelProps> = ({ownership}) => {
  const {hasDarkBg, accentColor} = useThemeContext();
  const {setTransitioning, space} = useSpaceContext();
  const {t} = useTranslation('components', {keyPrefix: 'space.aboutDigitalAssetPanel'});
  const cancelTransferInvitation = useApi(OwnershipsApi.cancelNftTransferInvitation);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const {pathname} = useLocation();
  const isOnTransferPage = /^\/space\/ownerships\/[^/]+\/transfer\/new?$/u.test(pathname);

  const onCancelTransfer = (): void => {
    if (ownership.activeDigitalAssetTransferInvitation === null) {
      return;
    }
    cancelTransferInvitation(ownership.id, ownership.activeDigitalAssetTransferInvitation.id)
      .then(async ownership => {
        await queryClient.invalidateQueries(ownershipsKeys.getByOwnershipId(ownership.id));
        navigate(getNftDetailRoute(ownership.id));
      })
      .catch(trackError);
  };

  const handleTransferClick = (): void => {
    setTransitioning({
      active: true,
      enterFrom: 'left',
    });
    navigate(getNewNftTransferRoute(ownership.id));
  };

  const handleShareClicked = (): void => {
    if (space !== undefined) {
      shareNft(
        space.merchant.name,
        ownership.digitalAsset.collection.title,
        ownership.digitalAsset.shareImageUrl,
        'nftDetailPage',
        {
          ownershipId: ownership.id,
        }
      ).catch(() => notify('error', t('shareError')));
    }
  };

  return (
    <div className="flex flex-col px-5 md:px-0">
      <div className="no-scrollbar mx-auto w-1/2 md:min-h-[150px] md:w-auto md:max-w-full md:overflow-auto">
        <DigitalAssetVisual
          digitalAssetImageFileKey={ownership.digitalAsset.imageFileKey}
          animationFileKey={ownership.digitalAsset.animationFileKey}
        />
      </div>

      <div className="mt-8 flex flex-col md:mt-5 md:pt-5">
        <DigitalAssetTitleBlock
          testId="digital-asset-detail-page"
          hasDarkBg={hasDarkBg}
          pretitle={t('tokenId', {tokenId: ownership.digitalAsset.tokenId})}
          title={ownership.digitalAsset.collection.title}
          subtitle={nftObtentionDate(ownership)}
          additionnalCssStyleOnAccent={{color: accentColor}}
        />

        {isOnTransferPage && (
          <div className="mt-8">
            <PerkAccessesDetails
              perkAccesses={ownership.digitalAsset.perkAccesses}
              helperText={t('perkAccessListHelperText')}
            />
          </div>
        )}

        {!isOnTransferPage && (
          <div className="mt-5 flex flex-col space-y-2">
            {ownership.activeDigitalAssetTransferInvitation?.status === 'active' ? (
              <SecondaryButton
                testId="cancel-send"
                text={t('cancelSendingBtnText')}
                onClick={onCancelTransfer}
                className="w-full"
                icon={<X className="h-5 w-5 text-red-500" aria-hidden="true" />}
                tracking={{namespace: 'digitalAssets.detail.cancelTransfer'}}
              />
            ) : ownership.canBeTransferred ? (
              <SecondaryButton
                testId="transfer"
                text={t('transfer')}
                onClick={handleTransferClick}
                className="w-full"
                icon={<ArrowRight className="h-5 w-5" aria-hidden="true" />}
                tracking={{namespace: 'digitalAssets.detail.transfer'}}
              />
            ) : (
              <Fragment />
            )}

            {canShare() && (
              <SecondaryButton
                testId="share"
                text={t('shareNft')}
                onClick={handleShareClicked}
                icon={<Share className="h-5 w-5" aria-hidden="true" />}
                className="w-full"
                tracking={{namespace: 'digitalAssets.detail.share'}}
              />
            )}

            {ownership.digitalAsset.nft?.blockExplorerUrl ||
            ownership.digitalAsset.nft?.openseaUrl ? (
              <DropdownComponent
                content={<DigitalAssetExternalLinks ownership={ownership} />}
                horizontalDirections={HoritzontalDirections.Left}
                verticalDirections={VerticalDirections.Top}
                leadingButton={
                  <Menu.Button className="w-full" as="div">
                    <SecondaryButton
                      testId="more"
                      text={t('more')}
                      className="w-full"
                      icon={<DotsThree className="h-5 w-5" aria-hidden="true" />}
                      tracking={{namespace: 'digitalAssets.detail.more'}}
                    />
                  </Menu.Button>
                }
              />
            ) : undefined}
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutDigitalAssetPanel;
